import { useState } from "react";

import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { useTranslation } from "@/core/i18n";

import { ConcernCategoryGroup } from "../utils/concernGrouping";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { TagCloudPicker } from "./TagCloudPicker";

interface PersonalisationConcernsProps {
  concernCategories: ConcernCategoryGroup[];
  onContinue?: (selections: Models<"DisplayedConcern">[]) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelections?: Models<"DisplayedConcern">[];

  prefix?: React.ReactNode;
}

export const PersonalisationConcerns = ({
  concernCategories,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText,
  skipText,
  progressPercentage,
  onBack,
  onClose,
  initialSelections,
  prefix,
}: PersonalisationConcernsProps) => {
  const { t } = useTranslation();
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const resolvedSkipText = skipText ?? t("personalisationConcerns.skipForNow");
  const [selections, setSelections] = useState<Models<"DisplayedConcern">[]>(
    initialSelections ?? [],
  );

  const handleSelectionChange = (selectedItems: Models<"DisplayedConcern">[]) => {
    setSelections(selectedItems);
  };

  const handleContinue = () => {
    onContinue?.(selections);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const hasSelections = selections.length > 0;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={!hasSelections}
            onClick={handleContinue}
          >
            {resolvedContinueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {resolvedSkipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">
        {t("personalisationConcerns.heading")}
      </PersonalisationHeading>
      {prefix}

      {concernCategories.map((categoryGroup, index) => {
        const categorySelections = selections.filter((selection) =>
          categoryGroup.categoryConcerns.some((concern) => concern.id === selection.id),
        );
        const selectedIds = categorySelections.map((item) => item.id);

        return (
          <div key={categoryGroup.category.id} className="flex flex-col gap-4">
            <PersonalisationHeading size="xs">
              {index + 1}. {categoryGroup.category.title}
            </PersonalisationHeading>
            <TagCloudPicker
              items={categoryGroup.categoryConcerns.map((concern) => ({
                id: concern.id,
                label: concern.name,
              }))}
              selectedIds={selectedIds}
              onSelectionChangeRequest={(selectedItems) => {
                // Remove current category selections
                const otherSelections = selections.filter(
                  (selection) =>
                    !categoryGroup.categoryConcerns.some((concern) => concern.id === selection.id),
                );

                // Add new category selections
                const newCategorySelections = categoryGroup.categoryConcerns.filter((concern) =>
                  selectedItems.some((item) => item.id === concern.id),
                );

                handleSelectionChange([...otherSelections, ...newCategorySelections]);
              }}
              selectedColor={selectedColor}
            />
          </div>
        );
      })}
    </PersonalisationScreenLayout>
  );
};
